import React, { useMemo } from "react";
import { Box, Drawer, IconButton } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import { PencilSimpleLine } from "@phosphor-icons/react";
import DrawerShell from "../Drawers/DrawerShell";

interface SidePanelDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  title?: string;
  icon?: React.ReactNode;
  pencilAction?: () => void;
  bodyPadding?: number | string;
}

/**
 * SidePanelDrawer renders a right-side overlay panel using the shared DrawerShell
 * so sidepanels keep the same behavior and styling as the PhaseTracker shell.
 */
const SidePanelDrawer: React.FC<SidePanelDrawerProps> = ({
  open,
  onClose,
  children,
  width = 400,
  title,
  icon,
  pencilAction,
  bodyPadding,
}) => {
  const theme: any = useTheme();

  const sidePanelTheme = useMemo(
    () =>
      createTheme(theme, {
        components: {
          MuiMenu: {
            styleOverrides: {
              root: { zIndex: 1400 },
            },
          },
          MuiPopover: {
            styleOverrides: {
              root: { zIndex: 1400 },
            },
          },
          MuiPopper: {
            styleOverrides: {
              root: { zIndex: 1400 },
            },
          },
        },
      }),
    [theme]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: `${width}px` },
          maxWidth: "100vw",
          height: { xs: "100%", md: "calc(100% - 32px)" },
          maxHeight: { xs: "100%", md: "calc(100% - 32px)" },
          m: { xs: 0, md: "16px" },
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <ThemeProvider theme={sidePanelTheme}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
            borderRadius: { xs: 0, md: "16px" },
          }}
        >
          <DrawerShell
            title={title}
            icon={icon}
            onClose={onClose}
            actions={
              pencilAction ? (
                <IconButton onClick={pencilAction} size="small" aria-label="Edit">
                  <PencilSimpleLine size={20} />
                </IconButton>
              ) : undefined
            }
            bodyPadding={bodyPadding}
          >
            {children}
          </DrawerShell>
        </Box>
      </ThemeProvider>
    </Drawer>
  );
};

export type { SidePanelDrawerProps };
export default SidePanelDrawer;